import { type ComponentProps, type ComponentType, memo, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import { type Clip } from '@air/api/types';
import { withByosImgixServerSign } from '@air/component-imgix-image';
import { type Marker, PlayerMedia, usePlayerReversePlayback, usePlayerStore } from '@air/component-player';
import { ClipUtils } from '@air/utils-clips';
import { cn } from '@air/utils-tailwind';

import { AnnotationsV2, type AnnotationsV2Props } from '~/components/AnnotationsV2/AnnotationsV2';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { useFullscreenContext } from '~/components/AssetModalV2/shared/context/FullscreenContext';
import { useFullscreenAutoHide } from '~/components/AssetModalV2/Visualizer/shared/hooks/useFullscreenAutoHide';
import { useVideoCommentRangePlaybackBounds } from '~/components/v2/components/asset-modal/visualizer/video/hooks/useVideoCommentRangePlaybackBounds';
import { useVideoVisualizerSearchParamSync } from '~/components/v2/components/asset-modal/visualizer/video/hooks/useVideoVisualizerSearchParamSync';
import { useVideoVisualizerShortcuts } from '~/components/v2/components/asset-modal/visualizer/video/hooks/useVideoVisualizerShortcuts';
import { VideoVisualizerFullscreenControls } from '~/components/v2/components/asset-modal/visualizer/video/VideoVisualizerFullscreenControls';
import { VideoVisualizerPlayPauseOverlay } from '~/components/v2/components/asset-modal/visualizer/video/VideoVisualizerPlayPauseOverlay';
import { VideoVisualizerProcessingBanner } from '~/components/v2/components/asset-modal/visualizer/video/VideoVisualizerProcessingBanner';
import { VideoVisualizerSeekbar } from '~/components/v2/components/asset-modal/visualizer/video/VideoVisualizerSeekbar';
import {
  getVideoContentBounds,
  type VideoContentBounds,
} from '~/components/v2/components/asset-modal/visualizer/video/videoCaptionOverlayUtils';
import { type Chapter } from '~/hooks/useAssetVersionChaptersList';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';
import { isMobileAgent } from '~/utils/PlatformHelpers';

export type VideoVisualizerProps = ComponentProps<'div'> &
  Pick<AnnotationsV2Props, 'AnnotationsAvatars' | 'AnnotationComment' | 'FloatingCommentForm'> & {
    canEditDiscussions?: boolean;
    CaptionOverlay?: ComponentType<{ contentBounds: VideoContentBounds | null }>;
    clip: Clip;
    chapters?: Chapter[];
    markers?: Marker[];
    showSeekbar?: boolean;
    SubtitlesButton?: ComponentType;
  };

export const VideoVisualizer = memo(
  ({
    AnnotationsAvatars,
    AnnotationComment,
    FloatingCommentForm,
    canEditDiscussions,
    CaptionOverlay,
    className,
    clip,
    chapters,
    markers,
    showSeekbar = true,
    SubtitlesButton,
    ...restOfProps
  }: VideoVisualizerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const fullscreenControlsRef = useRef<HTMLDivElement>(null);
    const { isFullscreen } = useFullscreenContext();
    const isAnnotationsEnabled = useAnnotationV2Store((state) => state.isAnnotationsEnabled);
    const { width: videoWidth, height: videoHeight, ref: videoResizeRef } = useResizeDetector<HTMLVideoElement>();
    const playing = usePlayerStore((state) => state.playing);
    const activeDiscussionId = useSelector(activeDiscussionIdParamSelector);

    const canDrawAnnotation = !!canEditDiscussions && !isFullscreen;
    const isViewingAnnotatedComment = !!activeDiscussionId;
    const isProcessing = ClipUtils.isProcessing(clip.status);
    const videoContentBounds = useMemo(
      () =>
        getVideoContentBounds({
          containerHeight: videoHeight ?? 0,
          containerWidth: videoWidth ?? 0,
          videoHeight: clip.height,
          videoWidth: clip.width,
        }),
      [clip.height, clip.width, videoHeight, videoWidth],
    );

    useFullscreenAutoHide({
      isFullscreen,
      containerRef,
      childrenRefs: [fullscreenControlsRef],
      timeoutMs: 2000,
    });
    useVideoVisualizerShortcuts({ clip });
    useVideoVisualizerSearchParamSync();
    useVideoCommentRangePlaybackBounds({ enabled: !isFullscreen });
    usePlayerReversePlayback();

    let visualizerControls = null;

    if (isFullscreen) {
      visualizerControls = (
        <VideoVisualizerFullscreenControls
          clip={clip}
          chapters={chapters}
          markers={markers}
          ref={fullscreenControlsRef}
          SubtitlesButton={SubtitlesButton}
        />
      );
    } else if (showSeekbar) {
      visualizerControls = <VideoVisualizerSeekbar clip={clip} chapters={chapters} markers={markers} />;
    }

    return (
      <div
        className={cn('flex size-full flex-col overflow-hidden', !isFullscreen && 'pt-16 pb-12', className)}
        ref={containerRef}
        {...restOfProps}
      >
        <div className="relative grow overflow-hidden">
          {isProcessing && <VideoVisualizerProcessingBanner />}
          <PlayerMedia
            className="size-full"
            clip={clip}
            poster={clip.assets.image ? withByosImgixServerSign(clip.assets.image) : undefined}
            ref={videoResizeRef}
            type={clip.type === 'audio' && isMobileAgent ? 'audio' : 'video'}
          />
          {CaptionOverlay && <CaptionOverlay contentBounds={videoContentBounds} />}
          {!isViewingAnnotatedComment && !isAnnotationsEnabled && <VideoVisualizerPlayPauseOverlay />}
          <AnnotationsV2
            AnnotationsAvatars={AnnotationsAvatars}
            AnnotationComment={AnnotationComment}
            FloatingCommentForm={FloatingCommentForm}
            width={videoWidth ?? 0}
            height={videoHeight ?? 0}
            pageNumber={-1}
            keyShortcutsEnabled={canDrawAnnotation}
            canDrawAnnotation={canDrawAnnotation}
            hideAnnotations={playing}
            data-testid="VIDEO_ANNOTATIONS_OVERLAY"
          />
        </div>

        {visualizerControls}
      </div>
    );
  },
);

VideoVisualizer.displayName = 'VideoVisualizer';
